"use client";
import React from "react";
import Link from "components/common/Link";
import Image from "components/common/Image";
import { get } from "utils/lodash";
import { formatPrice } from "utils/currencyFormatter";
import { useT } from "providers/I18nProvider";
import { shopAvatar, shortAddress, roundRating } from "./cardFormat";
import type { SlotDiscount } from "services/campaignPackage/formatter";

/**
 * Card ngang gọn: logo vuông bo góc + tên + mô tả + rating + CTA.
 * Dùng cho "Săn giờ đẹp" (variant slot) và "Địa điểm khác" (variant default).
 */
const ShopRowCard: React.FC<{
  shop: any;
  variant?: "default" | "slot";
  /** Deal khung giờ thật từ CampaignPackage type=slot (chỉ dùng cho variant slot) */
  deal?: SlotDiscount;
  className?: string;
}> = ({ shop, variant = "default", deal, className = "" }) => {
  const t = useT();
  const avatar = shopAvatar(shop);
  const rating = roundRating(get(shop, "rating"));
  const minPrice = Number(get(shop, "minPrice")) || 0;
  const category = get(shop, "mainShopCategory.name") || "";
  const address = shortAddress(get(shop, "displayedAddress"));
  const isSlot = variant === "slot";

  return (
    <Link
      href={`/${get(shop, "slug")}`}
      className={`group flex items-center gap-3 rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary bg-white p-2.5 md:p-3 border transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md ${
        isSlot
          ? "border-[#ffe6cc] hover:border-orange-300"
          : "border-gray-100 hover:border-primary/40"
      } ${className}`}
    >
      <Image
        src={avatar}
        alt={get(shop, "name") || ""}
        wrapClassName={`shrink-0 rounded-xl overflow-hidden bg-gray-100 ${
          isSlot ? "w-24 h-24" : "w-14 h-14 md:w-16 md:h-16"
        }`}
        sizes="96px"
      />
      <div className="min-w-0 flex-1">
        <p className="font-bold text-black01 truncate !mb-0">
          {get(shop, "name")}
        </p>
        <p className="text-xs text-gray-500 truncate !mb-0">
          {[category, address].filter(Boolean).join(" · ")}
        </p>
        {isSlot ? (
          <>
            {/* Dòng nổi bật: khung giờ giảm + % giảm — thông tin người dùng cần nhất */}
            <div className="flex items-center gap-2 mt-2">
              <span className="inline-flex items-center gap-1 rounded-lg bg-orange-500 text-white text-sm font-extrabold px-2.5 py-1 shadow-sm">
                {deal?.discountLabel}
              </span>
              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg bg-white border border-orange-200 text-orange-600 text-sm font-bold px-2.5 py-1">
                <span aria-hidden>🕐</span> {deal?.timeRange}
              </span>
            </div>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[12px] text-gray-500">
                {deal?.days ? t("home.slotDays", { days: deal.days }) : ""}
              </span>
              <span className="text-[12px] font-bold text-orange-600 inline-flex items-center gap-1 transition-all group-hover:gap-2">
                {t("home.bookArrow")} <span aria-hidden>→</span>
              </span>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 mt-0.5 md:mt-1">
            {/* Badge điểm LUÔN hiển thị để các card thẳng hàng; chưa có điểm → dấu "-" */}
            <span
              className={`rounded-full border px-2 py-0.5 text-[12px] font-bold ${
                rating
                  ? "border-[#d8f3f2] text-primary-ink"
                  : "border-gray-200 text-gray-400"
              }`}
            >
              {rating || "-"}
            </span>
            {minPrice > 0 && (
              <span className="text-[12px] text-gray-500">
                {t("home.fromPrice", { price: formatPrice(minPrice) })}
              </span>
            )}
            <span className="ml-auto text-[12px] font-bold text-primary-ink inline-flex items-center gap-1 transition-all group-hover:gap-2">
              {t("home.bookArrow")} <span aria-hidden>→</span>
            </span>
          </div>
        )}
      </div>
    </Link>
  );
};

export default ShopRowCard;
